import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  computed,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AddFormComponent,
  FormField,
} from '@shared/components/add-form/add-form.component';
import { VirtualWarehouseService } from '../services/virtual-warehouse.service';
import {
  VirtualWarehouseApiCreateRequest,
} from '../models/virtual-warehouse.model';
import { AuthService } from '@app/core/services/auth.service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { HttpErrorResponse } from '@angular/common/http';
import { PermissionService } from '@app/core/services/permission.service';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

const VIRTUAL_TYPE_CODES = [
  'QTY_VIRTUAL',
  'RATIO_VIRTUAL',
] as const;
const PHYSICAL_TYPE_CODES = ['MAIN', 'STORE', 'COD'] as const;

@Component({
  selector: 'app-virtual-warehouse-create',
  standalone: true,
  imports: [CommonModule, AddFormComponent, ToastModule, TranslateModule],
  providers: [MessageService],
  templateUrl: './virtual-warehouse-create.component.html',
  styleUrl: './virtual-warehouse-create.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualWarehouseCreateComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private virtualWarehouseService = inject(VirtualWarehouseService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private errorDialogService = inject(ErrorDialogService);
  private errorTranslatorService = inject(ErrorTranslatorService);
  private permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  loading = signal(false);
  submitting = signal(false);
  memberWarehouseOptions = signal<Array<{ label: string; value: number }>>([]);
  loadingMemberWarehouses = signal(false);
  initialData = signal<Record<string, unknown> | null>(null);
  private lastPhysicalType: string | null = null;
  private skipCanDeactivate = false;

  /** Edit mode when route has id param (virtual-page/edit/:id). */
  isEditMode = computed(() => !!this.route.snapshot.paramMap.get('id'));
  editId = computed(() => {
    const id = this.route.snapshot.paramMap.get('id');
    return id ? Number(id) : null;
  });

  wms_approve = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.virtual_warehouse.approve');
  });

  formConfig = signal({
    title: '',
    formFields: [] as FormField[],
    headerActions: [] as any[],
    tableSections: [] as any[],
  });

  @ViewChild(AddFormComponent) addFormRef?: AddFormComponent;

  ngOnInit(): void {
    this.updateTitleAndConfig();
    if (this.isEditMode() && this.editId()) {
      this.loadDetailForEdit();
    }
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateTitleAndConfig();
        this.cdr.markForCheck();
      });
  }

  private updateTitleAndConfig(): void {
    const titleKey = this.isEditMode()
      ? 'virtual_warehouse.create_form.edit_title'
      : 'virtual_warehouse.create_form.title';
    const title = this.translateService.instant(titleKey);
    this.formConfig.set({
      title,
      formFields: this.buildFormFields(),
      headerActions: this.buildHeaderActions(),
      tableSections: [],
    });
  }

  private loadDetailForEdit(): void {
    const id = this.editId();
    if (id == null) return;
    this.loading.set(true);
    this.virtualWarehouseService
      .getVirtualWarehouseDetailApi(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          const members = detail.members ?? [];
          const firstTypeCode = members[0]?.typeCode;
          const memberIds = members
            .map((m) => m.id)
            .filter((id): id is number => id != null);
          if (firstTypeCode) {
            this.lastPhysicalType = firstTypeCode;
            this.virtualWarehouseService
              .getLogicalWarehousesByType([firstTypeCode])
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (list) => {
                  this.memberWarehouseOptions.set(
                    list.map((w) => ({
                      label: `${w.name} (${w.code})`,
                      value: w.id,
                    })),
                  );
                  this.updateFormFields();
                  this.setInitialDataFromDetail(detail, memberIds);
                  this.loading.set(false);
                  this.cdr.markForCheck();
                },
                error: () => {
                  this.setInitialDataFromDetail(detail, memberIds);
                  this.loading.set(false);
                  this.cdr.markForCheck();
                },
              });
          } else {
            this.setInitialDataFromDetail(detail, memberIds);
            this.loading.set(false);
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private setInitialDataFromDetail(
    detail: { name?: string; code?: string; typeCode?: string; status?: boolean },
    memberIds: number[],
  ): void {
    const firstTypeCode = (detail as any).members?.[0]?.typeCode;
    this.initialData.set({
      name: detail.name ?? '',
      code: detail.code ?? '',
      typeCode: detail.typeCode ?? 'DEFAULT_VIRTUAL',
      status: detail.status ?? true,
      physicalWarehouseType: firstTypeCode ?? '',
      memberWarehouseIds: memberIds,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildFormFields(): FormField[] {
    const t = (key: string) => this.translateService.instant(key);
    const basicSection = t('virtual_warehouse.create_form.sections.basic_information');
    const physicalSection = t('virtual_warehouse.create_form.sections.member_warehouses');

    const virtualTypeOptions = VIRTUAL_TYPE_CODES.map((code) => ({
      label: t(`virtual_warehouse.warehouse.types.${code.toLowerCase()}`),
      value: code,
    }));
    const physicalTypeOptions = PHYSICAL_TYPE_CODES.map((code) => ({
      label: t(`virtual_warehouse.create_form.physical_type.${code.toLowerCase()}`),
      value: code,
    }));

    return [
      {
        label: t('virtual_warehouse.create_form.fields.name'),
        controlName: 'name',
        type: 'text',
        required: true,
        placeholder: t('virtual_warehouse.create_form.placeholders.enter_name'),
        maxLength: 255,
        section: basicSection,
      },
      {
        label: t('virtual_warehouse.create_form.fields.code'),
        controlName: 'code',
        type: 'text',
        required: true,
        disabled: this.isEditMode(),
        placeholder: t('virtual_warehouse.create_form.placeholders.enter_code'),
        maxLength: 255,
        section: basicSection,
      },
      {
        label: t('virtual_warehouse.create_form.fields.type'),
        controlName: 'typeCode',
        type: 'dropdown',
        required: true,
        disabled: this.isEditMode(),
        placeholder: t('virtual_warehouse.create_form.placeholders.select_type'),
        options: virtualTypeOptions,
        section: basicSection,
      },
      {
        label: t('virtual_warehouse.create_form.fields.status'),
        controlName: 'status',
        type: 'dropdown',
        required: true,
        disabled: !this.wms_approve(),
        placeholder: t('virtual_warehouse.create_form.placeholders.select_status'),
        defaultValue: true,
        options: [
          {
            label: t('virtual_warehouse.status.active'),
            value: true,
          },
          {
            label: t('virtual_warehouse.status.inactive'),
            value: false,
          },
        ],
        section: basicSection,
      },
      {
        label: t('virtual_warehouse.create_form.fields.physical_warehouse_type'),
        controlName: 'physicalWarehouseType',
        type: 'dropdown',
        required: false,
        disabled: this.isEditMode(),
        placeholder: t('virtual_warehouse.create_form.placeholders.select_type'),
        options: physicalTypeOptions,
        section: physicalSection,
      },
      {
        label: t('virtual_warehouse.create_form.fields.warehouse_name'),
        controlName: 'memberWarehouseIds',
        type: 'dropdown',
        required: false,
        multiple: true,
        options: this.memberWarehouseOptions(),
        placeholder: t('virtual_warehouse.create_form.placeholders.select_warehouse_names'),
        section: physicalSection,
      },
    ];
  }

  private buildHeaderActions(): any[] {
    const t = (key: string) => this.translateService.instant(key);
    return [
      {
        label: t('virtual_warehouse.create_form.actions.cancel'),
        severity: 'secondary',
        outlined: true,
        action: 'cancel',
      },
      {
        label: t('virtual_warehouse.create_form.actions.save'),
        severity: 'info',
        action: 'save',
      },
    ];
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.router.navigate(['/warehouse/virtual-page']);
      return;
    }
    if (event.action === 'save') {
      this.onSubmit(event.data);
    }
  }

  onFormValueChanges(formData: any): void {
    const physicalType = formData?.physicalWarehouseType as string | undefined;
    if (this.isEditMode()) {
      return;
    }

    if (physicalType && physicalType !== this.lastPhysicalType) {
      this.lastPhysicalType = physicalType;
      this.loadMemberWarehouseOptions(physicalType);
    } else if (!physicalType) {
      this.lastPhysicalType = null;
      this.memberWarehouseOptions.set([]);
      this.updateFormFields();
    }
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.submitting()) {
      return true;
    }
    return !(this.addFormRef?.form?.dirty ?? false);
  }

  private loadMemberWarehouseOptions(physicalType: string): void {
    this.loadingMemberWarehouses.set(true);
    this.virtualWarehouseService
      .getLogicalWarehousesByType([physicalType])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          const options = list.map((w) => ({
            label: `${w.name} (${w.code})`,
            value: w.id,
          }));
          this.memberWarehouseOptions.set(options);
          this.loadingMemberWarehouses.set(false);
          this.updateFormFields();
          this.cdr.markForCheck();
        },
        error: () => {
          this.memberWarehouseOptions.set([]);
          this.loadingMemberWarehouses.set(false);
          this.updateFormFields();
          this.cdr.markForCheck();
        },
      });
  }

  private updateFormFields(): void {
    this.formConfig.update((c) => ({
      ...c,
      formFields: this.buildFormFields(),
    }));
  }

  onSubmit(formData?: any): void {
    if (!formData) {
      this.submitting.set(false);
      return;
    }

    const memberIds = Array.isArray(formData.memberWarehouseIds)
      ? formData.memberWarehouseIds
      : [];
    if (
      !formData.name?.trim() ||
      formData.status === undefined ||
      memberIds.length === 0
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.currentLang === 'vi'
          ? 'Vui lòng điền tên, trạng thái và chọn ít nhất một kho hàng.'
          : 'Please fill name, status and select at least one warehouse.',
        life: 6000,
      });
      this.cdr.markForCheck();
      return;
    }

    if (!this.isEditMode()) {
      if (!formData.code?.trim() || !formData.typeCode) {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.currentLang === 'vi'
            ? 'Vui lòng điền đầy đủ các trường bắt buộc.'
            : 'Please fill in all required fields.',
          life: 6000,
        });
        this.cdr.markForCheck();
        return;
      }
    }

    this.submitting.set(true);
    const updatedBy =
      this.authService.currentUser()?.username ?? 'admin';

    if (this.isEditMode() && this.editId()) {
      this.virtualWarehouseService
        .updateVirtualWarehouseApi(this.editId()!, {
          name: formData.name.trim(),
          status: formData.status !== undefined ? formData.status : true,
          memberWarehouseIds: memberIds.length ? memberIds : [],
          updatedBy,
        })
        .subscribe({
          next: () => {
            this.submitting.set(false);
            this.skipCanDeactivate = true;
            this.addFormRef?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.currentLang === 'vi'
                ? 'Cập nhật kho ảo thành công.'
                : 'Virtual warehouse updated successfully.',
              life: 5000,
            });
            this.cdr.markForCheck();
            const returnUrl = this.route.snapshot.queryParams['returnUrl'];
            setTimeout(() => {
              if (returnUrl) {
                this.router.navigateByUrl(returnUrl);
              } else {
                this.router.navigate(['/warehouse/virtual-page']);
              }
            }, 1500);
          },
          error: (error: HttpErrorResponse) => {
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                error.error?.message,
                'virtual_warehouse.messages.error.failed_to_update',
              ),
            );
            this.submitting.set(false);
            this.cdr.markForCheck();
          },
        });
      return;
    }

    const request: VirtualWarehouseApiCreateRequest = {
      typeCode: formData.typeCode,
      name: formData.name.trim(),
      code: formData.code.trim(),
      status: formData.status !== undefined ? formData.status : true,
      memberWarehouseIds: memberIds.length ? memberIds : null,
      createdBy: updatedBy,
    };

    this.virtualWarehouseService.createVirtualWarehouseApi(request).subscribe({
      next: () => {
        this.submitting.set(false);
        this.skipCanDeactivate = true;
        this.addFormRef?.form?.markAsPristine();
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.currentLang === 'vi'
            ? 'Tạo kho ảo thành công.'
            : 'Virtual warehouse created successfully.',
          life: 5000,
        });
        this.cdr.markForCheck();
        const returnUrl = this.route.snapshot.queryParams['returnUrl'];
        setTimeout(() => {
          if (returnUrl) {
            this.router.navigateByUrl(returnUrl);
          } else {
            this.router.navigate(['/warehouse/virtual-page']);
          }
        }, 1500);
      },
      error: (error: HttpErrorResponse) => {
        this.errorDialogService.showError(
          this.errorTranslatorService.translate(
            error.error?.message,
            'virtual_warehouse.messages.error.failed_to_create',
          ),
        );
        this.submitting.set(false);
        this.cdr.markForCheck();
      },
    });
  }
}
